import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Snackbar,
  Stack,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Shield as ShieldIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { collection, deleteDoc, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
  DASHBOARD_PERMISSION_OPTIONS,
  DASHBOARD_PERMISSIONS,
  SUGGESTED_TEAM_TEMPLATES,
} from '../constants/dashboardAccess';

const createEmptyTeamForm = () => ({
  id: '',
  name: '',
  description: '',
  permissions: [DASHBOARD_PERMISSIONS.OVERVIEW],
});

const createEmptyAssignment = () => ({
  mode: 'full',
  teamIds: [],
  directPermissions: [],
});

const createEmptyMemberForm = () => ({
  name: '',
  email: '',
  password: '',
  phone: '',
  roleType: 'team_member',
  accessMode: 'restricted',
  teamIds: [],
  directPermissions: [],
});

const normalizeAdminAccess = (access) => {
  const mode = access?.mode === 'restricted' ? 'restricted' : 'full';

  const teamIds = Array.isArray(access?.teamIds)
    ? [...new Set(access.teamIds.filter((item) => typeof item === 'string' && item.trim()))]
    : [];

  const directPermissions = Array.isArray(access?.directPermissions)
    ? [
        ...new Set(
          access.directPermissions.filter(
            (item) => typeof item === 'string' && item.trim()
          )
        ),
      ]
    : [];

  return {
    mode,
    teamIds,
    directPermissions,
  };
};

const slugifyTeamId = (value) =>
  (value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const TeamAccessControl = () => {
  const { currentUser, refreshAccessControl } = useAuth();
  const [teams, setTeams] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [teamForm, setTeamForm] = useState(createEmptyTeamForm());
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [assignment, setAssignment] = useState(createEmptyAssignment());
  const [memberForm, setMemberForm] = useState(createEmptyMemberForm());
  const [creatingMember, setCreatingMember] = useState(false);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [showMemberPassword, setShowMemberPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const permissionGroups = useMemo(() => {
    return DASHBOARD_PERMISSION_OPTIONS.reduce((groups, option) => {
      const key = option.group || 'Other';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(option);
      return groups;
    }, {});
  }, []);

  const teamLabelById = useMemo(() => {
    const map = new Map();
    teams.forEach((team) => {
      map.set(team.id, team.name || team.id);
    });
    return map;
  }, [teams]);

  const permissionLabelByKey = useMemo(() => {
    const map = {};
    DASHBOARD_PERMISSION_OPTIONS.forEach((option) => {
      map[option.key] = option.label;
    });
    return map;
  }, []);

  const visibleTeams = useMemo(() => {
    const queryText = teamSearchTerm.trim().toLowerCase();
    if (!queryText) {
      return teams;
    }

    return teams.filter((team) => {
      const haystack = [
        team.name,
        team.description,
        ...(team.permissions || []),
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(queryText);
    });
  }, [teams, teamSearchTerm]);

  const visibleAdminUsers = useMemo(() => {
    const queryText = memberSearchTerm.trim().toLowerCase();
    if (!queryText) {
      return adminUsers;
    }

    return adminUsers.filter((adminUser) => {
      const haystack = [
        adminUser.name,
        adminUser.displayName,
        adminUser.email,
        adminUser.id,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(queryText);
    });
  }, [adminUsers, memberSearchTerm]);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const [teamSnapshot, adminSnapshot] = await Promise.all([
        getDocs(collection(db, 'adminTeams')),
        getDocs(query(collection(db, 'users'), where('roles.admin', '==', true))),
      ]);

      const loadedTeams = teamSnapshot.docs
        .map((item) => {
          const data = item.data() || {};
          return {
            id: item.id,
            name: data.name || item.id,
            description: data.description || '',
            permissions: Array.isArray(data.permissions) ? data.permissions : [],
            isActive: data.isActive !== false,
          };
        })
        .sort((left, right) => String(left.name).localeCompare(String(right.name)));

      const loadedAdmins = adminSnapshot.docs
        .map((item) => {
          const data = item.data() || {};
          return {
            id: item.id,
            ...data,
            adminAccess: normalizeAdminAccess(data.adminAccess),
          };
        })
        .sort((left, right) => {
          const leftLabel = left.name || left.displayName || left.email || left.id;
          const rightLabel = right.name || right.displayName || right.email || right.id;
          return String(leftLabel).localeCompare(String(rightLabel));
        });

      setTeams(loadedTeams);
      setAdminUsers(loadedAdmins);

      if (!selectedTeamId) {
        return;
      }

      const selected = loadedTeams.find((team) => team.id === selectedTeamId);
      if (!selected) {
        setSelectedTeamId('');
        setTeamForm(createEmptyTeamForm());
      }
    } catch (fetchError) {
      console.error('Failed to load team access data:', fetchError);
      setError('Failed to load teams and admin users. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const selectTeam = (team) => {
    setSelectedTeamId(team.id);
    setTeamForm({
      id: team.id,
      name: team.name || '',
      description: team.description || '',
      permissions: Array.isArray(team.permissions)
        ? [...new Set(team.permissions)]
        : [DASHBOARD_PERMISSIONS.OVERVIEW],
    });
  };

  const resetTeamForm = () => {
    setSelectedTeamId('');
    setTeamForm(createEmptyTeamForm());
  };

  const openTeamDialogForCreate = () => {
    resetTeamForm();
    setTeamDialogOpen(true);
  };

  const openTeamDialogForEdit = (team) => {
    selectTeam(team);
    setTeamDialogOpen(true);
  };

  const closeTeamDialog = () => {
    setTeamDialogOpen(false);
    resetTeamForm();
  };

  const toggleTeamPermission = (permission) => {
    setTeamForm((current) => {
      const currentPermissions = Array.isArray(current.permissions)
        ? current.permissions
        : [];
      const exists = currentPermissions.includes(permission);

      return {
        ...current,
        permissions: exists
          ? currentPermissions.filter((item) => item !== permission)
          : [...currentPermissions, permission],
      };
    });
  };

  const saveTeam = async () => {
    if (!teamForm.name.trim()) {
      openSnackbar('Team name is required.', 'error');
      return;
    }

    const permissions = [...new Set((teamForm.permissions || []).filter(Boolean))];
    if (permissions.length === 0) {
      openSnackbar('Select at least one dashboard permission.', 'error');
      return;
    }

    setSaving(true);
    try {
      const editing = Boolean(selectedTeamId);
      let teamId = editing ? selectedTeamId : slugifyTeamId(teamForm.name);

      if (!teamId) {
        teamId = `team-${Date.now()}`;
      }

      if (!editing && teams.some((team) => team.id === teamId)) {
        teamId = `${teamId}-${String(Date.now()).slice(-5)}`;
      }

      const now = new Date();
      const payload = {
        name: teamForm.name.trim(),
        description: teamForm.description.trim(),
        permissions,
        isActive: true,
        updatedAt: now,
        updatedBy: currentUser?.uid || currentUser?.email || null,
      };

      if (!editing) {
        payload.createdAt = now;
        payload.createdBy = currentUser?.uid || currentUser?.email || null;
      }

      await setDoc(doc(db, 'adminTeams', teamId), payload, { merge: true });
      openSnackbar(editing ? 'Team updated successfully.' : 'Team created successfully.');

      await fetchData();
      setTeamDialogOpen(false);
      resetTeamForm();

      if (currentUser?.uid) {
        await refreshAccessControl();
      }
    } catch (saveError) {
      console.error('Failed to save team:', saveError);
      openSnackbar('Failed to save team.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteTeam = async (teamRecord = null) => {
    const teamId = teamRecord?.id || selectedTeamId;
    if (!teamId) {
      return;
    }

    const selectedTeam =
      teamRecord || teams.find((team) => team.id === teamId) || null;
    const teamName = selectedTeam?.name || teamId;

    const confirmed = window.confirm(
      `Delete ${teamName}? Existing user assignments to this team will be ignored after deletion.`
    );
    if (!confirmed) {
      return;
    }

    setSaving(true);
    try {
      await deleteDoc(doc(db, 'adminTeams', teamId));
      openSnackbar('Team deleted.');
      if (selectedTeamId === teamId) {
        resetTeamForm();
      }
      setTeamDialogOpen(false);
      await fetchData();

      if (currentUser?.uid) {
        await refreshAccessControl();
      }
    } catch (deleteError) {
      console.error('Failed to delete team:', deleteError);
      openSnackbar('Failed to delete team.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const createSuggestedTeams = async () => {
    setSaving(true);
    try {
      const now = new Date();
      await Promise.all(
        SUGGESTED_TEAM_TEMPLATES.map((team) => {
          return setDoc(
            doc(db, 'adminTeams', team.id),
            {
              name: team.name,
              description: team.description,
              permissions: team.permissions,
              isActive: true,
              updatedAt: now,
              updatedBy: currentUser?.uid || currentUser?.email || null,
              createdAt: now,
              createdBy: currentUser?.uid || currentUser?.email || null,
            },
            { merge: true }
          );
        })
      );
      openSnackbar('Suggested teams are now ready.');
      await fetchData();
    } catch (seedError) {
      console.error('Failed to create suggested teams:', seedError);
      openSnackbar('Failed to create suggested teams.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetMemberForm = () => {
    setMemberForm(createEmptyMemberForm());
  };

  const openMemberDialog = () => {
    resetMemberForm();
    setShowMemberPassword(false);
    setMemberDialogOpen(true);
  };

  const closeMemberDialog = () => {
    setMemberDialogOpen(false);
    setShowMemberPassword(false);
    resetMemberForm();
  };

  const handleMemberInputChange = (event) => {
    const { name, value } = event.target;
    setMemberForm((current) => {
      if (name === 'roleType') {
        const nextRole = value === 'admin' ? 'admin' : 'team_member';
        return {
          ...current,
          roleType: nextRole,
          accessMode: nextRole === 'admin' ? 'full' : 'restricted',
        };
      }

      if (name === 'accessMode') {
        const nextMode = value === 'full' ? 'full' : 'restricted';
        return {
          ...current,
          accessMode: nextMode,
        };
      }

      return {
        ...current,
        [name]: value,
      };
    });
  };

  const toggleMemberTeam = (teamId) => {
    setMemberForm((current) => {
      const exists = current.teamIds.includes(teamId);
      return {
        ...current,
        teamIds: exists
          ? current.teamIds.filter((item) => item !== teamId)
          : [...current.teamIds, teamId],
      };
    });
  };

  const toggleMemberPermission = (permission) => {
    setMemberForm((current) => {
      const exists = current.directPermissions.includes(permission);
      return {
        ...current,
        directPermissions: exists
          ? current.directPermissions.filter((item) => item !== permission)
          : [...current.directPermissions, permission],
      };
    });
  };

  const handleCreateTeamMember = async () => {
    const name = memberForm.name.trim();
    const email = memberForm.email.trim().toLowerCase();
    const phone = memberForm.phone.trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!name) {
      openSnackbar('Team member name is required.', 'error');
      return;
    }

    if (!emailRegex.test(email)) {
      openSnackbar('Valid email is required.', 'error');
      return;
    }

    if ((memberForm.password || '').length < 6) {
      openSnackbar('Password must be at least 6 characters.', 'error');
      return;
    }

    const roleType = memberForm.roleType === 'admin' ? 'admin' : 'team_member';
    const accessMode = roleType === 'admin' ? 'full' : memberForm.accessMode;

    if (
      accessMode === 'restricted' &&
      memberForm.teamIds.length === 0 &&
      memberForm.directPermissions.length === 0
    ) {
      openSnackbar('Restricted team member needs at least one assigned team or task.', 'error');
      return;
    }

    setCreatingMember(true);
    try {
      const createTeamMember = httpsCallable(functions, 'createTeamMember');
      const result = await createTeamMember({
        name,
        email,
        password: memberForm.password,
        phone,
        roleType,
        adminAccess: {
          mode: accessMode,
          teamIds: memberForm.teamIds,
          directPermissions: memberForm.directPermissions,
        },
      });

      const createdName = result?.data?.name || name;
      openSnackbar(`Team member ${createdName} created successfully.`, 'success');
      setMemberDialogOpen(false);
      resetMemberForm();
      await fetchData();
    } catch (createError) {
      console.error('Failed to create team member:', createError);
      const fallbackMessage = 'Failed to create team member.';
      const rawMessage = createError?.message || fallbackMessage;
      const safeMessage = rawMessage.replace(/^Firebase:\s*/i, '').trim();
      openSnackbar(safeMessage || fallbackMessage, 'error');
    } finally {
      setCreatingMember(false);
    }
  };

  const openAssignmentDialog = (adminUser) => {
    setSelectedAdmin(adminUser);
    setAssignment(normalizeAdminAccess(adminUser.adminAccess));
    setAssignDialogOpen(true);
  };

  const toggleAssignmentTeam = (teamId) => {
    setAssignment((current) => {
      const exists = current.teamIds.includes(teamId);
      return {
        ...current,
        teamIds: exists
          ? current.teamIds.filter((item) => item !== teamId)
          : [...current.teamIds, teamId],
      };
    });
  };

  const toggleAssignmentPermission = (permission) => {
    setAssignment((current) => {
      const exists = current.directPermissions.includes(permission);
      return {
        ...current,
        directPermissions: exists
          ? current.directPermissions.filter((item) => item !== permission)
          : [...current.directPermissions, permission],
      };
    });
  };

  const saveAssignment = async () => {
    if (!selectedAdmin) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        mode: assignment.mode === 'restricted' ? 'restricted' : 'full',
        teamIds: [...new Set(assignment.teamIds)],
        directPermissions: [...new Set(assignment.directPermissions)],
        updatedAt: new Date(),
        updatedBy: currentUser?.uid || currentUser?.email || null,
      };

      await setDoc(
        doc(db, 'users', selectedAdmin.id),
        {
          adminAccess: payload,
        },
        { merge: true }
      );

      openSnackbar('Admin access updated successfully.');
      setAssignDialogOpen(false);
      await fetchData();

      if (selectedAdmin.id === currentUser?.uid) {
        await refreshAccessControl();
      }
    } catch (assignError) {
      console.error('Failed to update admin access:', assignError);
      openSnackbar('Failed to update admin access.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Team Access Control
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage team list, team member list, and role-based task access.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={saving || creatingMember}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            onClick={createSuggestedTeams}
            disabled={saving || creatingMember}
          >
            Add Suggested Teams
          </Button>
        </Stack>
      </Stack> */}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Typography variant="h6">Team List</Typography>
              <Typography variant="body2" color="text.secondary">
                View all teams and edit task permissions.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openTeamDialogForCreate}>
              Add Team
            </Button>
          </Stack>

          <TextField
            size="small"
            placeholder="Search team by name, description, or permission"
            value={teamSearchTerm}
            onChange={(event) => setTeamSearchTerm(event.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />

          {visibleTeams.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No teams found.
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {visibleTeams.map((team) => (
                <Card key={team.id} variant="outlined">
                  <CardContent>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      alignItems={{ xs: 'flex-start', md: 'center' }}
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {team.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {team.description || 'No description'}
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                          <Chip size="small" label={`${team.permissions.length} permissions`} color="primary" />
                          {team.permissions.slice(0, 3).map((permission) => (
                            <Chip
                              key={`${team.id}-${permission}`}
                              size="small"
                              label={permissionLabelByKey[permission] || permission}
                              variant="outlined"
                            />
                          ))}
                          {team.permissions.length > 3 && (
                            <Chip size="small" label={`+${team.permissions.length - 3} more`} variant="outlined" />
                          )}
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => openTeamDialogForEdit(team)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={() => deleteTeam(team)}
                          disabled={saving || creatingMember}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ mb: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <ShieldIcon color="primary" />
              <Typography variant="h6">Team Member List</Typography>
            </Stack>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openMemberDialog}>
              Add Team Member
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Show all members and use Configure Access to assign role and task permissions.
          </Typography>

          <TextField
            size="small"
            placeholder="Search member by name or email"
            value={memberSearchTerm}
            onChange={(event) => setMemberSearchTerm(event.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />

          {visibleAdminUsers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No admin users found in users collection.
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {visibleAdminUsers.map((adminUser) => {
                const displayName =
                  adminUser.name || adminUser.displayName || adminUser.email || adminUser.id;
                const access = normalizeAdminAccess(adminUser.adminAccess);

                return (
                  <Card variant="outlined" key={adminUser.id}>
                    <CardContent>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={1}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {adminUser.email || adminUser.id}
                          </Typography>
                          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                            <Chip
                              size="small"
                              color={access.mode === 'restricted' ? 'warning' : 'success'}
                              label={access.mode === 'restricted' ? 'Restricted' : 'Full access'}
                            />
                            {adminUser.isTeamMember && (
                              <Chip size="small" label="Team Member" variant="outlined" />
                            )}
                            {access.teamIds.map((teamId) => (
                              <Chip
                                key={`${adminUser.id}-${teamId}`}
                                size="small"
                                label={teamLabelById.get(teamId) || teamId}
                              />
                            ))}
                          </Stack>
                        </Box>
                        <Button variant="outlined" onClick={() => openAssignmentDialog(adminUser)}>
                          Configure Access
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={teamDialogOpen}
        onClose={closeTeamDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{selectedTeamId ? 'Edit Team' : 'Add Team'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Team Name"
            value={teamForm.name}
            onChange={(event) =>
              setTeamForm((current) => ({ ...current, name: event.target.value }))
            }
            placeholder="Refund Team"
            fullWidth
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="Role Description"
            value={teamForm.description}
            onChange={(event) =>
              setTeamForm((current) => ({ ...current, description: event.target.value }))
            }
            placeholder="This team processes only refund operations"
            multiline
            minRows={2}
            fullWidth
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Task Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Select exactly which dashboard tasks this team can access.
          </Typography>

          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {Object.entries(permissionGroups).map(([groupName, options]) => (
              <Card key={groupName} variant="outlined">
                <CardContent sx={{ py: '12px !important' }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>
                    {groupName}
                  </Typography>
                  <FormGroup>
                    {options.map((option) => (
                      <FormControlLabel
                        key={option.key}
                        control={
                          <Checkbox
                            checked={(teamForm.permissions || []).includes(option.key)}
                            onChange={() => toggleTeamPermission(option.key)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {option.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </FormGroup>
                </CardContent>
              </Card>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          {selectedTeamId && (
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => deleteTeam({ id: selectedTeamId, name: teamForm.name })}
              disabled={saving || creatingMember}
            >
              Delete
            </Button>
          )}
          <Button onClick={closeTeamDialog} disabled={saving || creatingMember}>
            Cancel
          </Button>
          <Button
            onClick={saveTeam}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving || creatingMember}
          >
            Save Team
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={memberDialogOpen}
        onClose={closeMemberDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Fill member details and assign role/task permissions.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                md: '1fr 1fr',
              },
              mb: 2,
            }}
          >
            <TextField
              label="Full Name"
              name="name"
              value={memberForm.name}
              onChange={handleMemberInputChange}
              placeholder="John Team"
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              value={memberForm.email}
              onChange={handleMemberInputChange}
              placeholder="john.team@example.com"
              fullWidth
            />
            <TextField
              label="Password"
              name="password"
              type={showMemberPassword ? 'text' : 'password'}
              value={memberForm.password}
              onChange={handleMemberInputChange}
              placeholder="Minimum 6 characters"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowMemberPassword((prev) => !prev)}
                      aria-label="toggle member password visibility"
                    >
                      {showMemberPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Phone Number"
              name="phone"
              value={memberForm.phone}
              onChange={handleMemberInputChange}
              placeholder="+61..."
              fullWidth
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                md: '1fr 1fr',
              },
              mb: 2,
            }}
          >
            <TextField
              select
              label="Role"
              name="roleType"
              value={memberForm.roleType}
              onChange={handleMemberInputChange}
              SelectProps={{ native: true }}
              fullWidth
            >
              <option value="team_member">Team Member (Restricted)</option>
              <option value="admin">Admin (Full Access)</option>
            </TextField>

            <TextField
              select
              label="Access Mode"
              name="accessMode"
              value={memberForm.roleType === 'admin' ? 'full' : memberForm.accessMode}
              onChange={handleMemberInputChange}
              SelectProps={{ native: true }}
              disabled={memberForm.roleType === 'admin'}
              fullWidth
            >
              <option value="restricted">Restricted</option>
              <option value="full">Full Access</option>
            </TextField>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Assign Teams
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Assign one or multiple teams to this member.
          </Typography>
          <FormGroup sx={{ mb: 2 }}>
            {teams.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No teams available yet. Create at least one team first.
              </Typography>
            ) : (
              teams.map((team) => (
                <FormControlLabel
                  key={`member-team-${team.id}`}
                  control={
                    <Checkbox
                      checked={memberForm.teamIds.includes(team.id)}
                      onChange={() => toggleMemberTeam(team.id)}
                      disabled={memberForm.roleType === 'admin'}
                    />
                  }
                  label={`${team.name} (${team.permissions.length} permissions)`}
                />
              ))
            )}
          </FormGroup>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Direct Task Permissions (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Add specific tasks directly if needed.
          </Typography>
          <Box sx={{ display: 'grid', gap: 1, mb: 2 }}>
            {DASHBOARD_PERMISSION_OPTIONS.map((permissionOption) => (
              <FormControlLabel
                key={`member-permission-${permissionOption.key}`}
                control={
                  <Checkbox
                    checked={memberForm.directPermissions.includes(permissionOption.key)}
                    onChange={() => toggleMemberPermission(permissionOption.key)}
                    disabled={memberForm.roleType === 'admin'}
                  />
                }
                label={permissionOption.label}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMemberDialog} disabled={creatingMember || saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTeamMember}
            disabled={creatingMember || saving}
          >
            {creatingMember ? 'Creating...' : 'Create Team Member'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Configure Admin Access</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedAdmin?.name || selectedAdmin?.displayName || selectedAdmin?.email || selectedAdmin?.id}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Access Mode
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant={assignment.mode === 'full' ? 'contained' : 'outlined'}
                onClick={() =>
                  setAssignment((current) => ({
                    ...current,
                    mode: 'full',
                  }))
                }
              >
                Full Access
              </Button>
              <Button
                variant={assignment.mode === 'restricted' ? 'contained' : 'outlined'}
                color={assignment.mode === 'restricted' ? 'warning' : 'primary'}
                onClick={() =>
                  setAssignment((current) => ({
                    ...current,
                    mode: 'restricted',
                  }))
                }
              >
                Restricted
              </Button>
            </Stack>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Team Assignment
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Assign one or multiple teams to this admin user.
          </Typography>
          <FormGroup sx={{ mb: 2 }}>
            {teams.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No teams available yet.
              </Typography>
            ) : (
              teams.map((team) => (
                <FormControlLabel
                  key={team.id}
                  control={
                    <Checkbox
                      checked={assignment.teamIds.includes(team.id)}
                      onChange={() => toggleAssignmentTeam(team.id)}
                    />
                  }
                  label={`${team.name} (${team.permissions.length} permissions)`}
                />
              ))
            )}
          </FormGroup>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Direct Permissions (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Use this when you want to grant extra tasks without creating a new team.
          </Typography>
          <FormGroup>
            {DASHBOARD_PERMISSION_OPTIONS.map((permissionOption) => (
              <FormControlLabel
                key={`direct-${permissionOption.key}`}
                control={
                  <Checkbox
                    checked={assignment.directPermissions.includes(permissionOption.key)}
                    onChange={() => toggleAssignmentPermission(permissionOption.key)}
                  />
                }
                label={permissionOption.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={saveAssignment}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            Save Access
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar({ open: false, message: '', severity: 'success' })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamAccessControl;
